import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { crm, getView, onViewChange, setView as setCrmView } from '../api/crm';

// Who is logged in (role, permissions, team) and which slice of the team they are
// looking at. Roles: admin | leader | executive | support | unauthorized. A support user
// is not a sales person: no leads, no permissions, only the search and requests screens.
const SalesContext = createContext(null);

export const SalesProvider = ({ children }) => {
  const [me, setMe] = useState(null); // null = loading
  const [view, setViewState] = useState(getView());

  const reload = useCallback(async () => {
    try {
      setMe(await crm('owner-access/me'));
    } catch (e) {
      setMe({ role: 'unauthorized', error: e.message });
    }
  }, []);

  useEffect(() => {
    reload();
    return onViewChange(setViewState);
  }, [reload]);

  const value = useMemo(() => {
    const role = me?.role || '';
    const isAdmin = role === 'admin';
    const isLeader = role === 'leader';
    return {
      me,
      loading: me === null,
      role,
      isAdmin,
      isLeader,
      isManager: isAdmin || isLeader,
      isExecutive: role === 'executive',
      isSupport: role === 'support',
      owner: me?.owner || '',
      name: me?.name || me?.owner || '',
      team: me?.team || [],
      can: (perm) => isAdmin || isLeader || Boolean(me?.permissions?.[perm]),
      view,
      setView: setCrmView,
      // A key that changes whenever the view changes: screens use it to refetch.
      viewKey: `${view.me ? 'me' : 'team'}|${view.member}|${view.team}`,
      reload,
    };
  }, [me, view, reload]);

  return <SalesContext.Provider value={value}>{children}</SalesContext.Provider>;
};

export const useSales = () => {
  const ctx = useContext(SalesContext);
  if (!ctx) throw new Error('useSales must be used within a SalesProvider');
  return ctx;
};
